import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Textarea,
} from "@mindstonehq/ui";
import { BugIcon, ImageIcon, ShieldAlertIcon, XIcon } from "lucide-react";
import { graphql } from "~/graphql/codegen";

const SUBMIT_CONTROLLED_BUG_REPORT = graphql(`
  mutation SubmitControlledBugReport($input: ControlledBugReportInput!) {
    submitControlledBugReport(input: $input) {
      environment
      status
      issueIdentifier
      issueUrl
      duplicateTitle
      slackDelivered
    }
  }
`);

const MAX_SCREENSHOT_BYTES = 5 * 1024 * 1024;
const ALLOWED_SCREENSHOT_TYPES = ["image/png", "image/jpeg", "image/webp"];

type Screenshot = {
  base64: string;
  contentType: "image/png" | "image/jpeg" | "image/webp";
  name: string;
  previewUrl: string;
};

function readScreenshot(file: File): Promise<Screenshot> {
  return new Promise((resolve, reject) => {
    if (!ALLOWED_SCREENSHOT_TYPES.includes(file.type)) {
      reject(new Error("type"));
      return;
    }
    if (file.size > MAX_SCREENSHOT_BYTES) {
      reject(new Error("size"));
      return;
    }
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("read"));
    reader.onload = () => {
      const previewUrl = String(reader.result ?? "");
      const base64 = previewUrl.split(",", 2)[1] ?? "";
      if (!base64) {
        reject(new Error("read"));
        return;
      }
      resolve({
        base64,
        contentType: file.type as Screenshot["contentType"],
        name: file.name,
        previewUrl,
      });
    };
    reader.readAsDataURL(file);
  });
}

export function ReportBugDialog({
  open,
  onOpenChange,
  mindspaceId,
  environment,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mindspaceId: string;
  environment: string;
}) {
  const { t } = useLingui();
  const [title, setTitle] = useState("");
  const [actualBehavior, setActualBehavior] = useState("");
  const [expectedBehavior, setExpectedBehavior] = useState("");
  const [reproductionSteps, setReproductionSteps] = useState("");
  const [screenshot, setScreenshot] = useState<Screenshot | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [fileError, setFileError] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState<{
    status: string;
    issueIdentifier: string;
    issueUrl: string;
    duplicateTitle: string;
  } | null>(null);
  const submissionGeneration = useRef(0);
  const [submitReport, { loading }] = useMutation(SUBMIT_CONTROLLED_BUG_REPORT);

  const isLocal = environment !== "s101" && environment !== "production";
  const isReady =
    title.trim().length >= 8 &&
    actualBehavior.trim().length >= 20 &&
    expectedBehavior.trim().length >= 10 &&
    reproductionSteps.trim().length >= 10 &&
    screenshot !== null &&
    confirmed;

  const reset = () => {
    submissionGeneration.current += 1;
    setTitle("");
    setActualBehavior("");
    setExpectedBehavior("");
    setReproductionSteps("");
    setScreenshot(null);
    setConfirmed(false);
    setFileError("");
    setSubmitError("");
    setResult(null);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && loading) return;
    onOpenChange(nextOpen);
    if (!nextOpen) reset();
  };

  const handleFile = async (file?: File) => {
    if (!file) return;
    setFileError("");
    try {
      setScreenshot(await readScreenshot(file));
    } catch (error) {
      const reason = error instanceof Error ? error.message : "read";
      setScreenshot(null);
      setFileError(
        reason === "size"
          ? t(msg`Choose an image smaller than 5 MB.`)
          : reason === "type"
            ? t(msg`Choose a PNG, JPEG, or WebP image.`)
            : t(msg`We couldn't read that image. Try another file.`),
      );
    }
  };

  const handleSubmit = async (createAnyway = false) => {
    if (!isReady || !screenshot) return;
    const generation = ++submissionGeneration.current;
    setSubmitError("");
    try {
      const response = await submitReport({
        variables: {
          input: {
            mindspaceId,
            pageUrl: window.location.href,
            title: title.trim(),
            actualBehavior: actualBehavior.trim(),
            expectedBehavior: expectedBehavior.trim(),
            reproductionSteps: reproductionSteps.trim(),
            screenshotBase64: screenshot.base64,
            screenshotContentType: screenshot.contentType,
            stagingChecksConfirmed: true,
            createAnyway,
          },
        },
      });
      if (!response.data?.submitControlledBugReport) {
        throw new Error("No report result");
      }
      if (generation !== submissionGeneration.current) return;
      setResult(response.data.submitControlledBugReport);
    } catch (error) {
      if (generation !== submissionGeneration.current) return;
      console.error("Failed to submit controlled bug report", error);
      setSubmitError(
        t(msg`The report couldn't be sent. Your notes are still here so you can try again.`),
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <BugIcon className="size-6 text-primary" aria-hidden="true" />
            <DialogTitle>
              <Trans>Report a product bug</Trans>
            </DialogTitle>
            <Badge variant={isLocal ? "secondary" : environment === "s101" ? "outline" : "destructive"}>
              {isLocal ? <Trans>Local preview</Trans> : environment === "s101" ? <Trans>S101 only</Trans> : <Trans>Production</Trans>}
            </Badge>
          </div>
          <DialogDescription>
            <Trans>Share a reproducible defect with the engineering team. This is separate from product feedback or support.</Trans>
          </DialogDescription>
        </DialogHeader>

        {result ? (
          <Alert>
            <BugIcon className="size-4" />
            <AlertTitle>
              {result.status === "LOCAL_PREVIEW" ? (
                <Trans>Local check complete</Trans>
              ) : result.status === "POSSIBLE_DUPLICATE" ? (
                <Trans>Possible duplicate found</Trans>
              ) : result.status === "LINEAR_ONLY" ? (
                <Trans>Created in Linear; Slack needs attention</Trans>
              ) : (
                <Trans>Bug reported</Trans>
              )}
            </AlertTitle>
            <AlertDescription>
              {result.status === "LOCAL_PREVIEW" ? (
                <Trans>No Linear issue or Slack message was created from your local environment.</Trans>
              ) : result.status === "POSSIBLE_DUPLICATE" ? (
                <>
                  <Trans>Review the existing issue before creating another:</Trans>{" "}
                  <a className="underline" href={result.issueUrl} target="_blank" rel="noreferrer">
                    {result.issueIdentifier}: {result.duplicateTitle}
                  </a>
                  .
                </>
              ) : (
                <>
                  <Trans>Your evidence was saved in</Trans>{" "}
                  <a className="underline" href={result.issueUrl} target="_blank" rel="noreferrer">
                    {result.issueIdentifier}
                  </a>
                  .
                </>
              )}
            </AlertDescription>
          </Alert>
        ) : (
          <div className="space-y-5">
            {isLocal ? (
              <Alert>
                <ShieldAlertIcon className="size-4" />
                <AlertTitle><Trans>Local reports never leave this environment</Trans></AlertTitle>
                <AlertDescription><Trans>You can test this form, but it will not create a Linear issue or notify Slack.</Trans></AlertDescription>
              </Alert>
            ) : environment === "s101" ? (
              <Alert>
                <ShieldAlertIcon className="size-4" />
                <AlertTitle><Trans>This report will be labelled S101 only</Trans></AlertTitle>
                <AlertDescription><Trans>Check whether test data, permissions, delayed jobs, or a disabled staging integration explains the behaviour before submitting.</Trans></AlertDescription>
              </Alert>
            ) : null}

            <div className="space-y-2">
              <Label htmlFor="bug-title"><Trans>Short summary</Trans></Label>
              <Input id="bug-title" aria-describedby="bug-title-help" value={title} maxLength={160} onChange={(event) => setTitle(event.target.value)} placeholder={t(msg`What is broken?`)} />
              <p id="bug-title-help" className="text-xs text-muted-foreground"><Trans>At least 8 characters.</Trans></p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-actual"><Trans>What happened?</Trans></Label>
              <Textarea id="bug-actual" aria-describedby="bug-actual-help" value={actualBehavior} maxLength={4000} rows={3} onChange={(event) => setActualBehavior(event.target.value)} />
              <p id="bug-actual-help" className="text-xs text-muted-foreground"><Trans>At least 20 characters.</Trans></p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-expected"><Trans>What did you expect?</Trans></Label>
              <Textarea id="bug-expected" aria-describedby="bug-expected-help" value={expectedBehavior} maxLength={4000} rows={3} onChange={(event) => setExpectedBehavior(event.target.value)} />
              <p id="bug-expected-help" className="text-xs text-muted-foreground"><Trans>At least 10 characters.</Trans></p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="bug-steps"><Trans>Steps to reproduce</Trans></Label>
              <Textarea id="bug-steps" aria-describedby="bug-steps-help" value={reproductionSteps} maxLength={6000} rows={4} onChange={(event) => setReproductionSteps(event.target.value)} placeholder={t(msg`1. Open… 2. Select… 3. See…`)} />
              <p id="bug-steps-help" className="text-xs text-muted-foreground"><Trans>At least 10 characters.</Trans></p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bug-screenshot"><Trans>Screenshot evidence</Trans></Label>
              <p className="text-sm text-muted-foreground"><Trans>Required. Remove personal or confidential information before attaching.</Trans></p>
              {screenshot ? (
                <div className="rounded-lg border bg-muted/30 p-3">
                  <img src={screenshot.previewUrl} alt={t(msg`Screenshot preview`)} className="max-h-56 w-full rounded object-contain" />
                  <div className="mt-2 flex items-center justify-between gap-3">
                    <span className="truncate text-sm">{screenshot.name}</span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setScreenshot(null)}>
                      <XIcon className="mr-1 size-4" />
                      <Trans>Remove</Trans>
                    </Button>
                  </div>
                </div>
              ) : (
                <Label htmlFor="bug-screenshot" className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed p-6 text-sm hover:bg-muted/50">
                  <ImageIcon className="size-5" />
                  <Trans>Choose screenshot</Trans>
                </Label>
              )}
              <Input id="bug-screenshot" className="sr-only" type="file" accept={ALLOWED_SCREENSHOT_TYPES.join(",")} onChange={(event) => void handleFile(event.target.files?.[0])} />
              {fileError && <p className="text-sm text-destructive" role="alert">{fileError}</p>}
            </div>

            <div className="flex items-start gap-3 rounded-lg border p-3">
              <Checkbox id="bug-confirmed" checked={confirmed} onCheckedChange={(value) => setConfirmed(value === true)} />
              <Label htmlFor="bug-confirmed" className="font-normal leading-5">
                <Trans>I reproduced this after refreshing and checked that it is not explained by test data, my permissions, a delayed job, or an unavailable staging integration.</Trans>
              </Label>
            </div>

            {submitError && <p className="text-sm text-destructive" role="alert">{submitError}</p>}
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" disabled={loading} onClick={() => handleOpenChange(false)}>
            {result ? <Trans>Close</Trans> : <Trans>Cancel</Trans>}
          </Button>
          {result?.status === "POSSIBLE_DUPLICATE" ? (
            <Button type="button" disabled={loading} onClick={() => void handleSubmit(true)}>
              {loading ? <Trans>Creating…</Trans> : <Trans>Create anyway</Trans>}
            </Button>
          ) : !result ? (
            <Button type="button" disabled={!isReady || loading} onClick={() => void handleSubmit()}>
              {loading ? <Trans>Sending…</Trans> : isLocal ? <Trans>Check local report</Trans> : <Trans>Create bug report</Trans>}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
